/* Quick-add drawer — collection-tile trigger + drawer lifecycle.
 * Approach B: no Loop widget inside the drawer. We render our own
 * Subscribe / One-time radios in sections/quick-add-drawer.liquid and
 * POST the resolved variant + selling_plan to /cart/add.js directly.
 *
 * Cart handoff: POSTs JSON with `sections: ['cart-drawer']` so the
 * response carries the updated drawer HTML, fires Maker's
 * `theme:cart:drawer:updateHtml` event so the cart-element swaps in
 * the new content, then triggers `theme:drawer:open` to show the cart.
 *
 * Historical bug (fixed 2026-04-24): event name was
 * `theme:cart:cart-drawer:updateHtml` — wrong. Maker's cart-element
 * (see theme.js:474) listens for `theme:cart:${this.view}:updateHtml`
 * where `view` is `"drawer"` — so the correct event name strips the
 * `cart-` prefix. Previously this meant the cart drawer opened empty
 * after add (visible as Maker's empty-state close icon — the "greyed
 * circle with a cross" the user reported).
 */

(function () {
  'use strict';

  var DRAWER_ROOT = '[data-tfmc-drawer-root]';
  var DRAWER_CONTENT = '[data-tfmc-drawer-content]';
  var TRIGGER_BUTTON = '.product-quick-add--button';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* Per-market currency + free-ship threshold come from Liquid-populated data attributes on
     the persistent drawer root (see snippets/quick-add-drawer.liquid). No hardcoded GBP /
     £50 anywhere — those would leak into international contexts. */
  function drawerSetting(name, fallback) {
    var root = $(DRAWER_ROOT);
    var v = root && root.dataset ? root.dataset[name] : null;
    return (v != null && v !== '') ? v : fallback;
  }

  var formatter = null;
  function formatMoney(cents) {
    if (!formatter) {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
        || drawerSetting('currencyCode', 'GBP');
      try {
        formatter = new Intl.NumberFormat(navigator.language || 'en-GB', {
          style: 'currency', currency: currency, minimumFractionDigits: 2
        });
      } catch (_) {
        /* Intl unavailable — render number-only rather than guess at a currency symbol. */
        formatter = { format: function (n) { return n.toFixed(2); } };
      }
    }
    return formatter.format(cents / 100);
  }
  function formatMoneyShort(cents) {
    /* Retired: previously stripped trailing .00 for UK. Locale-correct currency rendering
       beats the visual nicety for non-UK markets; integer GBP values now display as £14.00. */
    return formatMoney(cents);
  }

  function openDrawer() {
    var root = $(DRAWER_ROOT);
    if (!root) return;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tfmc-drawer-open');
    document.addEventListener('keydown', onEscape);
  }

  function closeDrawer() {
    var root = $(DRAWER_ROOT);
    if (!root) return;
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tfmc-drawer-open');
    document.removeEventListener('keydown', onEscape);
    var content = $(DRAWER_CONTENT);
    if (content) {
      setTimeout(function () { content.innerHTML = ''; }, 350);
    }
  }

  function onEscape(e) {
    if (e.key === 'Escape' || e.keyCode === 27) closeDrawer();
  }

  /* Capture-phase click listener so we pre-empt any Maker handlers on
     product-quick-add--button. Maker's <product-quick-add> custom element
     has been neutralised (tag switched to <div> in product-quick-add.liquid)
     so connectedCallback no longer runs, but stopImmediatePropagation here
     is still good defence-in-depth against menu-disclosure delegation. */
  function onTriggerClick(e) {
    var btn = e.target.closest && e.target.closest(TRIGGER_BUTTON);
    if (!btn) return;
    var wrap = btn.closest('[data-handle]');
    var handle = wrap && wrap.getAttribute('data-handle');
    if (!handle) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    fetchAndShow(handle);
  }

  function fetchAndShow(handle) {
    var content = $(DRAWER_CONTENT);
    if (!content) return;
    openDrawer();
    content.innerHTML = '<div class="tfmc-drawer-loading">Loading…</div>';
    fetch('/products/' + encodeURIComponent(handle) + '?section_id=quick-add-drawer', {
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        content.innerHTML = html;
        wireBody(content);
      })
      .catch(function (err) {
        content.innerHTML = '<div class="tfmc-drawer-error">Sorry, we couldn\'t load that. Please try again.</div>';
        if (window.console && console.error) console.error('[tfmc-drawer] fetch failed:', err);
      });
  }

  function wireBody(root) {
    var body = $('[data-tfmc-drawer-body]', root);
    if (!body) return;

    var data = body.dataset;
    var hasSubscribe = data.subscribeAvailable === 'true';
    var state = {
      mode: hasSubscribe ? 'subscribe' : 'onetime',
      jar: false
    };

    var radios = $$('input[name="tfmc-drawer-variant"]', body);
    var variantCards = $$('.tfmc-drawer-variant', body);
    var jarToggle = $('[data-tfmc-jar-toggle]', body);
    var jarInput = jarToggle ? jarToggle.querySelector('.tfmc-drawer-jar-input') : null;
    var subscribePanel = $('[data-tfmc-subscribe-panel]', body);
    var onetimePanel = $('[data-tfmc-onetime-panel]', body);
    var priceValue = $('[data-tfmc-price-value]', body);
    var priceCompare = $('[data-tfmc-price-compare]', body);
    var priceSave = $('[data-tfmc-price-save]', body);
    var trust = $('[data-tfmc-trust]', body);
    var atc = $('[data-tfmc-atc]', body);

    function sync() {
      variantCards.forEach(function (card) {
        card.classList.toggle('is-selected', card.getAttribute('data-variant-mode') === state.mode);
      });
      if (subscribePanel) subscribePanel.hidden = state.mode !== 'subscribe';
      if (onetimePanel) onetimePanel.hidden = state.mode !== 'onetime';
      if (jarToggle) jarToggle.classList.toggle('is-checked', state.jar);

      var price = 0;
      var compare = 0;
      if (state.mode === 'subscribe' && hasSubscribe) {
        price = parseInt(data.subscribePriceCents, 10) || 0;
        compare = parseInt(data.subscribeCompareCents, 10) || 0;
      } else {
        if (state.jar && data.starterOtVariantId) {
          price = parseInt(data.starterOtPriceCents, 10) || 0;
        } else {
          price = parseInt(data.pouchOnlyPriceCents, 10)
            || parseInt(data.starterOtPriceCents, 10)
            || 0;
        }
      }

      if (priceValue) priceValue.textContent = formatMoney(price);

      if (priceCompare) {
        if (state.mode === 'subscribe' && compare > price) {
          priceCompare.textContent = formatMoneyShort(compare);
          priceCompare.style.display = '';
        } else {
          priceCompare.style.display = 'none';
        }
      }

      if (priceSave) {
        if (state.mode === 'subscribe' && compare > price) {
          var isGBP = (document.body.dataset.currency || '').toUpperCase() === 'GBP';
          priceSave.textContent = isGBP ? 'You save 30%' : 'You save 15%';
          priceSave.style.display = '';
        } else {
          priceSave.style.display = 'none';
          priceSave.textContent = '';
        }
      }

      if (trust) {
        var freeShipCents = parseInt(drawerSetting('freeShipCents', '5000'), 10);
        var freeShip = state.mode === 'subscribe' || price >= freeShipCents;
        /* Trust strings are owned by Liquid on the trust element (data-trust-yes /
           data-trust-no). Per-market copy lives in sections/quick-add-drawer.liquid; this
           file no longer carries any UK-specific copy. */
        var yes = trust.dataset.trustYes || '';
        var no = trust.dataset.trustNo || '60-day money-back guarantee';
        trust.innerHTML = freeShip ? (yes || no) : no;
      }
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        state.mode = r.value;
        if (state.mode === 'subscribe') {
          state.jar = false;
          if (jarInput) jarInput.checked = false;
        }
        sync();
      });
    });

    variantCards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.closest('.tfmc-drawer-jar-input'))) return;
        var r = card.querySelector('input[name="tfmc-drawer-variant"]');
        if (r && !r.checked) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    if (jarInput) {
      jarInput.addEventListener('change', function () {
        state.jar = !!jarInput.checked;
        sync();
      });
    }

    if (atc) {
      atc.addEventListener('click', function () { submitCartAdd(body, state, atc); });
    }

    /* Inject desktop gallery arrows — hidden on mobile via CSS (@media ≥768px).
       Each arrow advances the gallery by finding the currently-active thumb
       and clicking its neighbour, reusing the mobile-gallery component's
       existing click-to-scroll behaviour (see mobile-gallery.js:_setupThumbs). */
    var galleryWrap = $('.tfmc-drawer-gallery-wrap', body);
    if (galleryWrap && !galleryWrap.querySelector('.tfmc-drawer-arrow')) {
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'tfmc-drawer-arrow tfmc-drawer-arrow-prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '&#8249;';
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'tfmc-drawer-arrow tfmc-drawer-arrow-next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '&#8250;';
      galleryWrap.appendChild(prev);
      galleryWrap.appendChild(next);

      function stepGallery(delta) {
        var thumbs = $$('.mg-thumb', galleryWrap);
        if (!thumbs.length) return;
        var idx = thumbs.findIndex(function (t) { return t.classList.contains('mg-thumb-active'); });
        if (idx < 0) idx = 0;
        var target = idx + delta;
        if (target < 0) target = 0;
        if (target >= thumbs.length) target = thumbs.length - 1;
        thumbs[target].click();
      }

      prev.addEventListener('click', function () { stepGallery(-1); });
      next.addEventListener('click', function () { stepGallery(1); });
    }

    sync();
  }

  function resolveCartItem(body, state) {
    var data = body.dataset;
    var id = null;
    var sellingPlan = null;

    if (state.mode === 'subscribe' && data.subscribeAvailable === 'true') {
      id = data.subscribeVariantId;
      sellingPlan = data.subscribePlanId;
    } else if (state.jar && data.starterOtVariantId) {
      id = data.starterOtVariantId;
    } else if (data.pouchOnlyVariantId) {
      id = data.pouchOnlyVariantId;
    } else if (data.starterOtVariantId) {
      id = data.starterOtVariantId;
    } else if (data.subscribeVariantId) {
      id = data.subscribeVariantId;
    }

    return { id: id, sellingPlan: sellingPlan };
  }

  function submitCartAdd(body, state, atc) {
    var item = resolveCartItem(body, state);
    if (!item.id) return;

    var payload = { id: parseInt(item.id, 10), quantity: 1 };
    if (item.sellingPlan) payload.selling_plan = parseInt(item.sellingPlan, 10);

    var body_json = {
      items: [payload],
      sections: ['cart-drawer']
    };

    atc.disabled = true;
    var originalLabel = atc.textContent;
    atc.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body_json)
    })
      .then(function (r) {
        return r.text().then(function (text) {
          var parsed = null;
          try { parsed = JSON.parse(text); } catch (_) {}
          if (!r.ok) {
            var msg = (parsed && (parsed.description || parsed.message)) || ('HTTP ' + r.status);
            var err = new Error(msg);
            err.status = r.status;
            throw err;
          }
          return parsed || {};
        });
      })
      .then(function (json) {
        return afterCartAdd(json).catch(function (e) {
          // Non-fatal — the item IS in the cart; just fall back to hard nav.
          if (window.console && console.error) console.error('[tfmc-drawer] cart handoff failed:', e);
          closeDrawer();
          setTimeout(function () { window.location.href = '/cart'; }, 200);
        });
      })
      .catch(function (err) {
        atc.disabled = false;
        atc.textContent = originalLabel;
        if (window.console && console.error) console.error('[tfmc-drawer] cart-add failed:', err);
        var msg = 'Sorry, we couldn\'t add that to the cart.';
        if (err && err.message && /sold out|unavailable/i.test(err.message)) {
          msg = 'Sorry, this option is sold out.';
        }
        window.alert(msg);
      });
  }

  function afterCartAdd(json) {
    var hasTrigger = typeof window.trigger === 'function';

    /* Feed Maker's cart-element the updated section HTML.
       Event: theme:cart:drawer:updateHtml (NOT theme:cart:cart-drawer:updateHtml —
       see file header comment). */
    if (hasTrigger && json && json.sections && json.sections['cart-drawer']) {
      try {
        window.trigger('theme:cart:drawer:updateHtml', {
          section: json.sections['cart-drawer'],
          load_transitions: true
        });
      } catch (_) {}
    }

    /* Refresh the cart count badge and empty-state indicator. */
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) {
          var countEl = document.querySelector('[data-cart-count]');
          if (countEl) countEl.setAttribute('data-cart-count', cart.item_count);
          var viewport = document.querySelector('.layout--viewport');
          if (viewport) viewport.setAttribute('data-cart-empty', cart.item_count === 0);
        }
        if (hasTrigger) {
          try { window.trigger('theme:cart:updated'); } catch (_) {}
        }
        /* Close our drawer, then open Maker's cart drawer once ours has slid out. */
        closeDrawer();
        if (hasTrigger) {
          setTimeout(function () {
            try {
              window.trigger('theme:transition:reset:cart-drawer');
              window.trigger('theme:drawer:open', { side: 'right', view: 'cart-drawer' });
            } catch (_) {
              window.location.href = '/cart';
            }
          }, 380);
        } else {
          setTimeout(function () { window.location.href = '/cart'; }, 200);
        }
      });
  }

  /* Event wiring */
  document.addEventListener('click', onTriggerClick, true);
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-tfmc-drawer-scrim]')) closeDrawer();
    if (e.target.closest('.tfmc-drawer-close')) closeDrawer();
  });
})();
